"use client";

import React, { useId } from "react";
import { cn } from "@/lib/utils";
import { useIsLaunchTheme } from "@/theme/ThemeProvider";

export interface LogoProps {
	className?: string;
	showText?: boolean;
	size?: number;
	invert?: boolean;
}

export const Logo: React.FC<LogoProps> = ({
	className,
	showText = true,
	size = 52,
	invert = false,
}) => {
	// Read the theme here rather than taking a prop. It used to be opt-in, and
	// 10 of 13 call sites never passed it — the onboarding screens rendered a
	// neon tile while the rest of the page was themed. Reading it internally
	// makes the logo correct everywhere by construction.
	const isLaunch = useIsLaunchTheme();
	// The gradient id must be unique per instance. The header renders a desktop
	// and a mobile Logo at once (one CSS-hidden), so a shared constant id put two
	// copies in the DOM — url(#id) resolves to the first, which on mobile sits in
	// a display:none subtree, so the tile painted nothing. useId is SSR-safe; its
	// output is stripped to [A-Za-z0-9_-] because React has emitted both ":r0:"
	// and "«r0»" across versions and neither is safe inside url(#...).
	const instanceId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
	const gradientId = `automint-logo-launch-gradient-${instanceId}`;

	// Tokens, not literals, so the tile follows ANY theme — the neon brand, a
	// future campaign, anything. The launch campaign is the exception: its tile
	// is a gradient sweep, which a single colour token cannot express.
	const rectFill = isLaunch
		? `url(#${gradientId})`
		: invert
			? "var(--am-accent-500)"
			: "var(--am-neutral-950)";
	const pathFill = isLaunch
		? "#2D1218"
		: invert
			? "var(--am-neutral-950)"
			: "var(--am-accent-500)";
	const sizeStyle = size
		? ({ "--logo-size": `${size}px` } as React.CSSProperties)
		: undefined;

	return (
		<div className={cn("flex items-center gap-2", className)} style={sizeStyle}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 36 36"
				fill="none"
				className="flex-shrink-0"
				shapeRendering="geometricPrecision"
				style={{
					width: "var(--logo-size, 36px)",
					height: "var(--logo-size, 36px)",
				}}
			>
				{isLaunch && (
					<defs>
						<linearGradient
							id={gradientId}
							x1="2.5"
							y1="31.5"
							x2="33.5"
							y2="6.5"
							gradientUnits="userSpaceOnUse"
						>
							<stop offset="0.067" stopColor="#A85A70" />
							<stop offset="0.5161" stopColor="#DDBCC5" />
							<stop offset="0.9837" stopColor="#F3ECEC" />
						</linearGradient>
					</defs>
				)}
				<rect width="36" height="36" rx="6.46214" fill={rectFill} />
				<path
					d="M29.7226 18.1377C29.7226 19.1218 29.6196 20.0819 29.4242 21.0074C29.4254 21.0098 29.4242 21.0121 29.423 21.0133C29.3792 21.2274 29.3294 21.4402 29.2738 21.6494C28.9149 23.0281 28.3488 24.3194 27.6122 25.4912C27.4594 25.7351 27.2984 25.9742 27.1302 26.2074C26.7275 26.7682 26.2846 27.2967 25.8038 27.7881C25.5563 28.0416 25.3005 28.2843 25.034 28.5187C24.3175 29.1464 23.5347 29.7012 22.6986 30.1675C21.6813 30.7391 20.5834 31.1803 19.4276 31.472V28.4876C19.97 28.3118 20.4934 28.0918 20.9932 27.8335C21.8506 27.3935 22.6406 26.8399 23.3464 26.1931C23.6034 25.9575 23.8497 25.7088 24.0842 25.4493C24.7675 24.6901 25.3455 23.8328 25.7955 22.9001C26.043 22.3872 26.2526 21.8503 26.4184 21.2955C25.85 21.3936 25.291 21.5167 24.7415 21.665C23.6306 21.9615 22.56 22.3561 21.5368 22.838C21.1957 22.9994 20.8606 23.1692 20.5301 23.3497C20.1547 23.5542 19.7876 23.7706 19.4276 23.999C18.9385 24.3098 18.4647 24.6411 18.0064 24.9926C17.5481 24.6411 17.0744 24.3087 16.5853 24.0002C16.2253 23.7706 15.8581 23.5542 15.4839 23.3509C15.1535 23.1704 14.8183 22.9994 14.4772 22.8392C13.454 22.3573 12.3822 21.9627 11.2702 21.665C10.7207 21.5179 10.1617 21.3936 9.59323 21.2967C9.75903 21.8515 9.96865 22.3872 10.2174 22.9001C10.6674 23.8328 11.2453 24.6901 11.9298 25.4505C12.1631 25.71 12.4083 25.9575 12.6665 26.1943C13.3723 26.8411 14.1622 27.3935 15.0196 27.8347C15.5194 28.0918 16.0429 28.3118 16.5853 28.4876V31.472C15.4294 31.1803 14.3316 30.7391 13.3143 30.1675C12.4782 29.7012 11.6965 29.1476 10.98 28.5187C10.7136 28.2855 10.4566 28.0428 10.2114 27.7893C9.72824 27.2967 9.28295 26.767 8.8791 26.205C8.71212 25.973 8.55224 25.7351 8.39947 25.4912C7.66284 24.3194 7.09675 23.0281 6.73909 21.6506C6.68343 21.4414 6.63251 21.2286 6.58869 21.0145C6.5875 21.0121 6.58632 21.0098 6.5875 21.0074C6.3921 20.0819 6.28906 19.1218 6.28906 18.1377C7.25307 18.1377 8.20169 18.1999 9.13373 18.3207C9.34927 18.3482 9.56481 18.378 9.77798 18.4127C11.1032 18.6232 12.3893 18.9532 13.6257 19.3896C14.649 19.7507 15.6378 20.1847 16.5853 20.6857C16.725 20.7599 16.8624 20.8352 16.9998 20.9117C17.0566 20.9428 17.1147 20.9751 17.1715 21.0074C17.4534 21.1676 17.7329 21.335 18.0076 21.5072C18.2824 21.335 18.5607 21.1676 18.8425 21.0074C18.8994 20.9751 18.9574 20.9428 19.0143 20.9117C19.1516 20.8352 19.289 20.7599 19.4276 20.6869C20.375 20.1847 21.3639 19.7507 22.3859 19.3896C23.6223 18.9532 24.9097 18.6244 26.2349 18.4127C26.448 18.3792 26.6624 18.3482 26.8779 18.3207C27.8088 18.1999 28.7586 18.1377 29.7226 18.1377Z"
					fill={pathFill}
				/>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M26.8458 17.2671C27.7754 17.1511 28.7229 17.0901 29.6833 17.0889C29.6739 16.9598 29.662 16.8318 29.649 16.7027C29.5353 15.5895 29.289 14.5158 28.9266 13.4983C28.9266 13.4947 28.9254 13.4911 28.923 13.4887C28.5737 12.5046 28.1165 11.5732 27.5646 10.7075C27.5481 10.6812 27.5303 10.6549 27.5125 10.6286C27.3905 10.4373 27.2626 10.2508 27.13 10.0678C26.4206 9.079 25.5821 8.19061 24.6418 7.42776C24.5719 7.36917 24.5021 7.31297 24.431 7.25917C22.9708 6.11609 21.2737 5.26835 19.4274 4.80203C18.9631 4.68366 18.4894 4.5904 18.0062 4.52344C17.523 4.5904 17.0493 4.68366 16.5851 4.80203C14.7388 5.26835 13.0417 6.11609 11.5815 7.25917C11.5104 7.31297 11.4405 7.36917 11.3707 7.42776C10.4291 8.19061 9.59067 9.0802 8.87891 10.0702C8.74746 10.2532 8.62074 10.4385 8.49994 10.6286C8.48099 10.6549 8.46441 10.6812 8.44783 10.7075C7.89595 11.5732 7.43763 12.5046 7.08827 13.4887C7.0859 13.4911 7.08471 13.4947 7.08471 13.4983C6.72232 14.5158 6.47599 15.5895 6.3623 16.7027C6.34927 16.8318 6.33743 16.9598 6.32795 17.0889C7.28841 17.0901 8.23584 17.1511 9.16551 17.2671C9.17972 17.0769 9.19985 16.8892 9.22472 16.7027C9.35855 15.6672 9.63804 14.6784 10.0407 13.7577L10.0409 13.7572C10.0412 13.7574 10.0416 13.7576 10.0419 13.7578C11.5803 13.9909 13.0713 14.3652 14.5043 14.8686C15.2137 15.1161 15.9077 15.3959 16.5863 15.7056C17.0683 15.9244 17.5408 16.1599 18.0063 16.4086H18.0074C18.4729 16.1587 18.9466 15.9244 19.4286 15.7056C20.106 15.3959 20.8 15.1161 21.5082 14.8686C22.9412 14.3652 24.4322 13.9909 25.9706 13.7578C26.3732 14.6784 26.6527 15.6673 26.7866 16.7027C26.8114 16.8892 26.8316 17.0769 26.8458 17.2671ZM24.34 11.1215L24.3398 11.1212C24.1989 10.9526 24.0532 10.7876 23.9016 10.6286C22.6818 9.33727 21.1493 8.34844 19.4274 7.78646C18.9667 7.63461 18.4918 7.51504 18.0062 7.42776H18.005C17.5195 7.51504 17.0458 7.63461 16.5851 7.78646C14.8631 8.34844 13.3307 9.33727 12.1097 10.6286C11.9593 10.7875 11.8138 10.9524 11.6729 11.1209C11.6732 11.121 11.6735 11.1212 11.6739 11.1213C13.3662 11.4346 15.0076 11.8925 16.5863 12.4844C17.0659 12.6625 17.5396 12.8538 18.0074 13.0571C18.4752 12.8538 18.949 12.6625 19.4286 12.4832C21.0069 11.8926 22.648 11.4347 24.34 11.1215Z"
					fill={pathFill}
				/>
			</svg>
			{showText && (
				<span
					className={cn(
						"automint-logo-text",
						invert ? "!text-white" : "!text-automint-white",
					)}
				>
					AutoMint
				</span>
			)}
		</div>
	);
};
